import { SelectedMode } from 'store/buildingStore'
import { AnnotationType } from 'types/building'

import { ALLOWED_ANNOTATION_TYPES_BY_MODE } from '../constants'

/**
 * AnnotationEntity
 * Domain Entity representing an annotation and its behavioral rules.
 */
export class AnnotationEntity {
  id: string | number

  type: AnnotationType

  setAnnotationId?: number

  raw: any

  constructor(data: any) {
    this.raw = data || {}
    this.id = data?.id
    this.type = Number(data?.type) as AnnotationType
    this.setAnnotationId = data?.set_annotation_id || data?.setAnnotationId
  }

  /**
   * Casts raw data to an AnnotationEntity instance.
   */
  static from(data: any): AnnotationEntity {
    if (data instanceof AnnotationEntity) return data
    return new AnnotationEntity(data)
  }

  /**
   * Whether this annotation is a Comment annotation.
   */
  get isComment(): boolean {
    return this.type === AnnotationType.COMMENT
  }

  /**
   * Whether this annotation is a Memo annotation.
   */
  get isMemo(): boolean {
    return this.type === AnnotationType.MEMO
  }

  /**
   * Whether this annotation is a Tour media annotation (Image, Movie, Info).
   */
  get isTourMedia(): boolean {
    return (
      this.type === AnnotationType.IMAGE ||
      this.type === AnnotationType.MOVIE ||
      this.type === AnnotationType.INFO
    )
  }

  /**
   * Whether this annotation belongs to a set annotation.
   */
  get isSetAnnotation(): boolean {
    return Boolean(this.setAnnotationId)
  }

  /**
   * Determines if this annotation can be clicked to open its detail/edit modal
   * based on the active toolbar mode and building context.
   *
   * Rules:
   * 1. Comments can ALWAYS be opened (view/reply) regardless of active toolbar mode.
   * 2. Tour-specific modes only apply in Tour buildings (type 10).
   * 3. Non-comment annotations must match the allowed types defined for the active mode.
   */
  isClickableInMode(mode: SelectedMode, buildingType?: number): boolean {
    // 1. Comments can always be opened regardless of active toolbar mode
    if (this.isComment) {
      return true
    }

    // 2. Tour-specific modes only apply in Tour buildings (type 10)
    const isTourMode =
      mode === SelectedMode.TOUR_ANNOTATION ||
      mode === SelectedMode.TOUR_MEMO_ANNOTATION
    if (isTourMode && buildingType !== 10) {
      return false
    }

    // 3. Verify against allowed annotation types for current toolbar mode
    const allowedTypes = ALLOWED_ANNOTATION_TYPES_BY_MODE[mode]
    if (!allowedTypes) {
      return false
    }

    return allowedTypes.includes(this.type)
  }
}

export default AnnotationEntity
